/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import type {
	Operation,
	NotificationAction,
	useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation as useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$data,
	useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutationVariables as useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$variables,
} from './__types__/useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation';

const useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation = gql`
	mutation useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation(
		$contentId: ID!
		$tasks: [IndividualInlineTaskNotificationInput]!
	) {
		createInlineTaskNotification(input: { contentId: $contentId, tasks: $tasks }) {
			contentId
			success
			errors {
				message
			}
		}
	}
`;
type CreateInlineTaskNotificationMutationProps = {
	contentId: string;
	setError: React.Dispatch<React.SetStateAction<Error | null>>;
	showDontNotifyFailedFlag: () => void;
};

export type UpdatedTaskItem = {
	operation: Operation;
	recipientAccountId: string;
	taskId: string;
	notificationAction: NotificationAction;
	recipientMentionLocalId: string;
};

type SendCreateInlineTaskNotificationsProps = {
	tasks?: UpdatedTaskItem[];
};

export const useCreateInlineTaskNotificationMutation = ({
	contentId,
	setError,
	showDontNotifyFailedFlag,
}: CreateInlineTaskNotificationMutationProps) => {
	const [createInlineTaskNotificationMutation, { error: createTaskNotificationError }] =
		useMutation<
			useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$data,
			useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$variables
		>(useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendCreateInlineTaskNotifications = useCallback(
		({ tasks }: SendCreateInlineTaskNotificationsProps) => {
			const fireAnalyticsEvent = (action: 'succeeded' | 'failed') => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'mentionsNotifications',
						action,
						actionSubject: 'createInlineTaskMention',
						attributes: {
							contentId,
							mentions: tasks?.map((task) => ({
								taskId: task.taskId,
								mentionId: task.recipientMentionLocalId,
								mentionedUserId: task.recipientAccountId,
							})),
						},
					},
				}).fire();
			};

			const taskItemsToNotify = fg('confluence_frontend_task_mentions_id')
				? tasks?.map((task) => ({
						...task,
						taskId: '0',
					}))
				: tasks;

			// NCS draft syncs need to happen prior to mutation in order for
			// email content to show up. A 15 second buffer after user closes
			// the flag notification will give draft syncs a chance to happen.
			setTimeout(() => {
				createInlineTaskNotificationMutation({
					variables: { contentId, tasks: taskItemsToNotify },
				})
					.then(() => {
						fireAnalyticsEvent('succeeded');
					})
					.catch((error) => {
						fireAnalyticsEvent('failed');
						setError(error);
					});
			}, 15000);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentId, createAnalyticsEvent, createInlineTaskNotificationMutation],
	);

	const cancelInlineTaskNotifications = useCallback(
		({ tasks }: SendCreateInlineTaskNotificationsProps) => {
			const taskItemsToNotify = fg('confluence_frontend_task_mentions_id')
				? tasks?.map((task) => ({
						...task,
						taskId: '0',
					}))
				: tasks;

			createInlineTaskNotificationMutation({
				variables: { contentId, tasks: taskItemsToNotify },
			}).catch(() => {
				showDontNotifyFailedFlag();
			});
		},
		[contentId, createInlineTaskNotificationMutation, showDontNotifyFailedFlag],
	);

	return {
		sendCreateInlineTaskNotifications,
		cancelInlineTaskNotifications,
		createTaskNotificationError,
	};
};
